import React, { useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { Modal } from '../ui/Modal';
import { getCrewSkillSummary } from '../../utils/dispatchHelpers';

const btnSecondary = 'bg-slate-100 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-200 font-medium text-sm';
const btnPrimary = 'bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium text-sm disabled:opacity-50';

export default function CrewReorderModal({ crew, onSave, onClose }) {
  const active = crew.filter(m => m.active !== false).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
  const [order, setOrder] = useState(active.map(c => c.id));
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const { show } = useToast();

  const getCrewById = (id) => crew.find(c => c.id === id);

  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', idx);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIdx(idx);
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setOverIdx(null); return; }
    setOrder(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(null);
    setOverIdx(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(order);
      onClose();
    } catch (e) {
      // Toast, not alert (QA M2 NEW-9 native-dialog sweep).
      show('Error saving order: ' + (e.message || 'Unknown error'), 'error');
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Reorder Crew" onClose={onClose} width="max-w-sm">
      <p className="text-xs text-slate-500 mb-3">Drag crew members to reorder. Changes apply to the dispatch board.</p>
      <div className="space-y-1">
        {order.map((id, idx) => {
          const c = getCrewById(id);
          if (!c) return null;
          return (
            <div key={id}
              draggable
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragLeave={() => setOverIdx(null)}
              onDrop={(e) => handleDrop(e, idx)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-grab active:cursor-grabbing transition-all ${
                dragIdx === idx ? 'opacity-40 border-blue-400 bg-blue-50' :
                overIdx === idx ? 'border-blue-400 bg-blue-50 scale-[1.02]' :
                'border-slate-200 bg-white hover:bg-slate-50'
              }`}
            >
              <span className="text-slate-400 text-sm font-mono w-5">{idx + 1}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-300 flex-shrink-0"><circle cx="9" cy="5" r="1"/><circle cx="9" cy="12" r="1"/><circle cx="9" cy="19" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="15" cy="19" r="1"/></svg>
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.display_color }}></div>
              <span className="text-sm font-medium flex-1">{c.name}</span>
              <span className="text-[10px] text-slate-400">{getCrewSkillSummary(c)}</span>
            </div>
          );
        })}
      </div>
      <div className="flex justify-end gap-3 mt-4">
        <button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Order'}</button>
      </div>
    </Modal>
  );
}
